/**
 * デモモード用モックデータ
 *
 * 各業種に特化したサンプルデータを提供
 * 本番環境には一切影響を与えない
 */

import type { Project, Todo, Team } from '../lib/supabase'
import type { Milestone, MilestoneTemplate, MockTeamMember } from './milestoneData'
import type { TodoFile } from '../lib/fileStorage'
import type { TodoImage } from '../lib/imageStorage'

// デモユーザー情報
export const demoUser = {
  id: 'demo-user-001',
  email: 'demo@tastack.app',
  nickname: 'デモユーザー',
  avatar_url: null,
}

// デモ用サンプル画像データ（タスクIDをキーに）
// 各デモタイプで最低1つのタスクにサムネイル画像を表示
// 注: このオブジェクトはデモ中に変更可能（ローカル画像追加用）
export let demoImages: Record<string, TodoImage[]> = {
  // ===== 映像制作デモ =====
  'demo-todo-2': [
    {
      id: 'demo-image-1',
      todo_id: 'demo-todo-2',
      user_id: demoUser.id,
      image_url: '/img/imagePC01.jpg',
      file_name: 'ロケハン写真1.jpg',
      file_size: 1024000,
      display_order: 0,
      created_at: new Date().toISOString(),
    },
    {
      id: 'demo-image-2',
      todo_id: 'demo-todo-2',
      user_id: demoUser.id,
      image_url: '/img/imagePhone01.jpg',
      file_name: 'ロケハン写真2.jpg',
      file_size: 850000,
      display_order: 1,
      created_at: new Date().toISOString(),
    },
  ],
  'demo-todo-5': [
    {
      id: 'demo-image-3',
      todo_id: 'demo-todo-5',
      user_id: demoUser.id,
      image_url: '/img/imagePC01.jpg',
      file_name: 'サムネイル候補.jpg',
      file_size: 512000,
      display_order: 0,
      created_at: new Date().toISOString(),
    },
  ],
  // ===== Web制作デモ =====
  'demo-web-todo-2': [
    {
      id: 'demo-web-image-1',
      todo_id: 'demo-web-todo-2',
      user_id: demoUser.id,
      image_url: '/img/imagePC01.jpg',
      file_name: 'デザインカンプ.jpg',
      file_size: 1200000,
      display_order: 0,
      created_at: new Date().toISOString(),
    },
  ],
  'demo-web-todo-7': [
    {
      id: 'demo-web-image-2',
      todo_id: 'demo-web-todo-7',
      user_id: demoUser.id,
      image_url: '/img/imagePhone01.jpg',
      file_name: 'LPデザイン案.jpg',
      file_size: 900000,
      display_order: 0,
      created_at: new Date().toISOString(),
    },
  ],
  // ===== 一般事務デモ =====
  'demo-office-todo-4': [
    {
      id: 'demo-office-image-1',
      todo_id: 'demo-office-todo-4',
      user_id: demoUser.id,
      image_url: '/img/imagePC01.jpg',
      file_name: '会場写真.jpg',
      file_size: 800000,
      display_order: 0,
      created_at: new Date().toISOString(),
    },
  ],
  // ===== 個人利用デモ =====
  'demo-personal-todo-1': [
    {
      id: 'demo-personal-image-1',
      todo_id: 'demo-personal-todo-1',
      user_id: demoUser.id,
      image_url: '/img/imagePC01.jpg',
      file_name: '企画資料.jpg',
      file_size: 750000,
      display_order: 0,
      created_at: new Date().toISOString(),
    },
  ],
  'demo-personal-todo-6': [
    {
      id: 'demo-personal-image-2',
      todo_id: 'demo-personal-todo-6',
      user_id: demoUser.id,
      image_url: '/img/imagePhone02.jpg',
      file_name: '読書メモ.jpg',
      file_size: 600000,
      display_order: 0,
      created_at: new Date().toISOString(),
    },
  ],
}

// デモ用サンプルファイルデータ（タスクIDをキーに）
// 各デモタイプでファイル添付機能を見せる
export const demoFiles: Record<string, TodoFile[]> = {
  // ===== 映像制作デモ =====
  'demo-todo-1': [
    {
      id: 'demo-file-1',
      todo_id: 'demo-todo-1',
      user_id: demoUser.id,
      file_url: '/sample.pdf',
      file_name: '台本_初稿.pdf',
      file_size: 256000,
      file_type: 'application/pdf',
      file_category: 'document',
      display_order: 0,
      created_at: new Date().toISOString(),
    },
  ],
  'demo-todo-3': [
    {
      id: 'demo-file-2',
      todo_id: 'demo-todo-3',
      user_id: demoUser.id,
      file_url: '/sample.pdf',
      file_name: '撮影スケジュール.pdf',
      file_size: 180000,
      file_type: 'application/pdf',
      file_category: 'document',
      display_order: 0,
      created_at: new Date().toISOString(),
    },
  ],
  // ===== Web制作デモ =====
  'demo-web-todo-1': [
    {
      id: 'demo-web-file-1',
      todo_id: 'demo-web-todo-1',
      user_id: demoUser.id,
      file_url: '/sample.pdf',
      file_name: 'ワイヤーフレーム.pdf',
      file_size: 320000,
      file_type: 'application/pdf',
      file_category: 'document',
      display_order: 0,
      created_at: new Date().toISOString(),
    },
  ],
  // ===== 一般事務デモ =====
  'demo-office-todo-1': [
    {
      id: 'demo-office-file-1',
      todo_id: 'demo-office-todo-1',
      user_id: demoUser.id,
      file_url: '/sample.pdf',
      file_name: '経費精算書.pdf',
      file_size: 150000,
      file_type: 'application/pdf',
      file_category: 'document',
      display_order: 0,
      created_at: new Date().toISOString(),
    },
  ],
  // ===== 個人利用デモ =====
  'demo-personal-todo-1': [
    {
      id: 'demo-personal-file-1',
      todo_id: 'demo-personal-todo-1',
      user_id: demoUser.id,
      file_url: '/sample.pdf',
      file_name: '企画書.pdf',
      file_size: 280000,
      file_type: 'application/pdf',
      file_category: 'document',
      display_order: 0,
      created_at: new Date().toISOString(),
    },
  ],
}

// デモモード用の画像追加関数（ローカルファイルから）
export function addDemoImage(todoId: string, image: TodoImage): void {
  if (!demoImages[todoId]) {
    demoImages[todoId] = []
  }
  demoImages[todoId].push(image)
}

// デモモード用の画像削除関数
export function removeDemoImage(todoId: string, imageId: string): void {
  if (demoImages[todoId]) {
    demoImages[todoId] = demoImages[todoId].filter(img => img.id !== imageId)
    if (demoImages[todoId].length === 0) {
      delete demoImages[todoId]
    }
  }
}

// デモデータをリセット（デモ終了時に呼び出し）
export function resetDemoImages(): void {
  // 初期データに戻す（追加された画像を削除）
  // Data URL（blob:）で始まる画像を削除
  Object.keys(demoImages).forEach(todoId => {
    demoImages[todoId] = demoImages[todoId].filter(img => !img.image_url.startsWith('data:'))
  })
}

// 今日の日付を基準にした日付生成ヘルパー
const today = new Date()
const formatDate = (daysOffset: number): string => {
  const date = new Date(today)
  date.setDate(date.getDate() + daysOffset)
  return date.toISOString().split('T')[0]
}

// =====================
// 映像制作会社向けデモデータ
// =====================
export const videoProductionData = {
  team: {
    id: 'demo-team-video',
    name: '映像制作チーム',
    description: 'CM・Web動画・イベント映像の制作チーム',
    owner_id: demoUser.id,
    created_at: formatDate(-30),
    updated_at: formatDate(0),
  } as Team,

  teamMembers: [
    { id: 'member-1', name: '田中', avatarColor: '#6366f1' },
    { id: 'member-2', name: '鈴木', avatarColor: '#ec4899' },
    { id: 'member-3', name: '山本', avatarColor: '#10b981' },
    { id: 'member-4', name: '佐藤', avatarColor: '#f59e0b' },
  ] as MockTeamMember[],

  projects: [
    // チームプロジェクト
    {
      id: 'demo-proj-cm',
      name: 'A社 CM制作',
      description: '30秒TVCMの企画・制作',
      color: '#6366f1',
      is_active: true,
      show_thumbnail: true,
      show_in_all_projects: true,
      user_id: demoUser.id,
      created_at: formatDate(-20),
      updated_at: formatDate(0),
      display_order: 1000,
      is_team_project: true,
      team_id: 'demo-team-video',
    },
    {
      id: 'demo-proj-web',
      name: 'B社 Web動画',
      description: 'YouTube向けプロモーション動画',
      color: '#ec4899',
      is_active: true,
      show_thumbnail: true,
      show_in_all_projects: true,
      user_id: demoUser.id,
      created_at: formatDate(-15),
      updated_at: formatDate(0),
      display_order: 2000,
      is_team_project: true,
      team_id: 'demo-team-video',
    },
    {
      id: 'demo-proj-event',
      name: 'C社 イベント映像',
      description: '展示会用映像コンテンツ',
      color: '#10b981',
      is_active: true,
      show_thumbnail: true,
      show_in_all_projects: true,
      user_id: demoUser.id,
      created_at: formatDate(-10),
      updated_at: formatDate(0),
      display_order: 3000,
      is_team_project: true,
      team_id: 'demo-team-video',
    },
    // 個人プロジェクト
    {
      id: 'demo-proj-personal-video',
      name: '個人案件メモ',
      description: '個人で受けた小規模案件の管理',
      color: '#f59e0b',
      is_active: true,
      show_thumbnail: true,
      show_in_all_projects: true,
      user_id: demoUser.id,
      created_at: formatDate(-5),
      updated_at: formatDate(0),
      display_order: 4000,
      is_team_project: false,
      team_id: null,
    },
  ] as Project[],

  todos: [
    // A社 CM制作
    {
      id: 'demo-todo-1',
      project_id: 'demo-proj-cm',
      title: '台本初稿作成',
      description: 'クライアントヒアリング内容をもとに台本を作成',
      priority: 3,
      status: 'completed' as const,
      start_date: formatDate(-10),
      due_date: formatDate(-5),
      created_at: formatDate(-10),
      updated_at: formatDate(-5),
      completed_at: formatDate(-5),
      deleted_at: null,
      display_order: 1000,
      thumbnail_image_id: null,
      thumbnail_size: 'small' as const,
      user_id: demoUser.id,
      assigned_to_user_id: 'member-1',
      assignment_status: 'assigned' as const,
      progress_percentage: 100,
      gantt_sort_order: 1,
    },
    {
      id: 'demo-todo-2',
      project_id: 'demo-proj-cm',
      title: '台本修正・クライアント確認',
      description: 'フィードバックを反映して修正',
      priority: 3,
      status: 'in_progress' as const,
      start_date: formatDate(-4),
      due_date: formatDate(2),
      created_at: formatDate(-4),
      updated_at: formatDate(0),
      completed_at: null,
      deleted_at: null,
      display_order: 2000,
      thumbnail_image_id: 'demo-image-1',
      thumbnail_size: 'small' as const,
      user_id: demoUser.id,
      assigned_to_user_id: 'member-1',
      assignment_status: 'assigned' as const,
      progress_percentage: 50,
      gantt_sort_order: 2,
    },
    {
      id: 'demo-todo-3',
      project_id: 'demo-proj-cm',
      title: '撮影準備・ロケハン',
      description: '撮影場所の下見と機材準備',
      priority: 2,
      status: 'in_progress' as const,
      start_date: formatDate(-2),
      due_date: formatDate(5),
      created_at: formatDate(-2),
      updated_at: formatDate(0),
      completed_at: null,
      deleted_at: null,
      display_order: 3000,
      thumbnail_image_id: null,
      thumbnail_size: 'small' as const,
      user_id: demoUser.id,
      assigned_to_user_id: 'member-2',
      assignment_status: 'assigned' as const,
      progress_percentage: 25,
      gantt_sort_order: 3,
    },
    {
      id: 'demo-todo-4',
      project_id: 'demo-proj-cm',
      title: '撮影',
      description: '本撮影（2日間）',
      priority: 3,
      status: 'pending' as const,
      start_date: formatDate(6),
      due_date: formatDate(8),
      created_at: formatDate(-2),
      updated_at: formatDate(0),
      completed_at: null,
      deleted_at: null,
      display_order: 4000,
      thumbnail_image_id: null,
      thumbnail_size: 'small' as const,
      user_id: demoUser.id,
      assigned_to_user_id: 'member-2',
      assignment_status: 'assigned' as const,
      progress_percentage: 0,
      gantt_sort_order: 4,
    },
    {
      id: 'demo-todo-5',
      project_id: 'demo-proj-cm',
      title: '編集・MA作業',
      description: '映像編集と音声収録',
      priority: 3,
      status: 'pending' as const,
      start_date: formatDate(9),
      due_date: formatDate(18),
      created_at: formatDate(-2),
      updated_at: formatDate(0),
      completed_at: null,
      deleted_at: null,
      display_order: 5000,
      thumbnail_image_id: 'demo-image-3',
      thumbnail_size: 'small' as const,
      user_id: demoUser.id,
      assigned_to_user_id: 'member-3',
      assignment_status: 'assigned' as const,
      progress_percentage: 0,
      gantt_sort_order: 5,
    },
    // B社 Web動画
    {
      id: 'demo-todo-6',
      project_id: 'demo-proj-web',
      title: '企画書作成',
      description: 'コンセプト・構成案の作成',
      priority: 3,
      status: 'completed' as const,
      start_date: formatDate(-8),
      due_date: formatDate(-3),
      created_at: formatDate(-8),
      updated_at: formatDate(-3),
      completed_at: formatDate(-3),
      deleted_at: null,
      display_order: 1000,
      thumbnail_image_id: null,
      thumbnail_size: 'small' as const,
      user_id: demoUser.id,
      assigned_to_user_id: 'member-3',
      assignment_status: 'assigned' as const,
      progress_percentage: 100,
      gantt_sort_order: 1,
    },
    {
      id: 'demo-todo-7',
      project_id: 'demo-proj-web',
      title: 'デザイン・絵コンテ制作',
      description: 'ビジュアルデザインと絵コンテ作成',
      priority: 2,
      status: 'in_progress' as const,
      start_date: formatDate(-2),
      due_date: formatDate(7),
      created_at: formatDate(-2),
      updated_at: formatDate(0),
      completed_at: null,
      deleted_at: null,
      display_order: 2000,
      thumbnail_image_id: null,
      thumbnail_size: 'small' as const,
      user_id: demoUser.id,
      assigned_to_user_id: 'member-1',
      assignment_status: 'assigned' as const,
      progress_percentage: 50,
      gantt_sort_order: 2,
    },
    {
      id: 'demo-todo-8',
      project_id: 'demo-proj-web',
      title: '動画編集',
      description: 'モーショングラフィックス制作',
      priority: 2,
      status: 'pending' as const,
      start_date: formatDate(8),
      due_date: formatDate(15),
      created_at: formatDate(-2),
      updated_at: formatDate(0),
      completed_at: null,
      deleted_at: null,
      display_order: 3000,
      thumbnail_image_id: null,
      thumbnail_size: 'small' as const,
      user_id: demoUser.id,
      assigned_to_user_id: 'member-4',
      assignment_status: 'assigned' as const,
      progress_percentage: 0,
      gantt_sort_order: 3,
    },
    // C社 イベント映像
    {
      id: 'demo-todo-9',
      project_id: 'demo-proj-event',
      title: 'コンテンツ構成検討',
      description: '展示会ブースでの上映形式を確認',
      priority: 2,
      status: 'completed' as const,
      start_date: formatDate(-5),
      due_date: formatDate(-2),
      created_at: formatDate(-5),
      updated_at: formatDate(-2),
      completed_at: formatDate(-2),
      deleted_at: null,
      display_order: 1000,
      thumbnail_image_id: null,
      thumbnail_size: 'small' as const,
      user_id: demoUser.id,
      assigned_to_user_id: 'member-4',
      assignment_status: 'assigned' as const,
      progress_percentage: 100,
      gantt_sort_order: 1,
    },
    {
      id: 'demo-todo-10',
      project_id: 'demo-proj-event',
      title: '素材収集・撮影',
      description: '製品撮影と素材収集',
      priority: 3,
      status: 'in_progress' as const,
      start_date: formatDate(-1),
      due_date: formatDate(4),
      created_at: formatDate(-1),
      updated_at: formatDate(0),
      completed_at: null,
      deleted_at: null,
      display_order: 2000,
      thumbnail_image_id: null,
      thumbnail_size: 'small' as const,
      user_id: demoUser.id,
      assigned_to_user_id: 'member-2',
      assignment_status: 'assigned' as const,
      progress_percentage: 50,
      gantt_sort_order: 2,
    },
    // 個人案件メモ（個人プロジェクト）
    {
      id: 'demo-todo-11',
      project_id: 'demo-proj-personal-video',
      title: '知人の結婚式撮影',
      description: '友人からの依頼。当日撮影と簡単な編集',
      priority: 2,
      status: 'in_progress' as const,
      start_date: formatDate(7),
      due_date: formatDate(14),
      created_at: formatDate(-3),
      updated_at: formatDate(0),
      completed_at: null,
      deleted_at: null,
      display_order: 1000,
      thumbnail_image_id: null,
      thumbnail_size: 'small' as const,
      user_id: demoUser.id,
      assigned_to_user_id: null,
      assignment_status: 'unassigned' as const,
      progress_percentage: 0,
      gantt_sort_order: 1,
    },
    {
      id: 'demo-todo-12',
      project_id: 'demo-proj-personal-video',
      title: '機材メンテナンス',
      description: 'カメラのセンサークリーニング予約',
      priority: 1,
      status: 'pending' as const,
      start_date: formatDate(3),
      due_date: formatDate(5),
      created_at: formatDate(0),
      updated_at: formatDate(0),
      completed_at: null,
      deleted_at: null,
      display_order: 2000,
      thumbnail_image_id: null,
      thumbnail_size: 'small' as const,
      user_id: demoUser.id,
      assigned_to_user_id: null,
      assignment_status: 'unassigned' as const,
      progress_percentage: 0,
      gantt_sort_order: 2,
    },
  ] as Todo[],

  milestones: [
    {
      id: 'demo-ms-1',
      projectId: 'demo-proj-cm',
      templateId: 'tpl-1',
      title: '台本チェック',
      startDate: formatDate(3),
      iconType: 'emoji' as const,
      iconValue: '📋',
      color: '#6366f1',
      sortOrder: 1,
    },
    {
      id: 'demo-ms-2',
      projectId: 'demo-proj-cm',
      templateId: 'tpl-2',
      title: '撮影日',
      startDate: formatDate(6),
      endDate: formatDate(8),
      iconType: 'emoji' as const,
      iconValue: '🎬',
      color: '#10b981',
      sortOrder: 2,
    },
    {
      id: 'demo-ms-3',
      projectId: 'demo-proj-cm',
      templateId: 'tpl-3',
      title: '納品',
      startDate: formatDate(20),
      iconType: 'emoji' as const,
      iconValue: '🚀',
      color: '#ef4444',
      sortOrder: 3,
    },
    {
      id: 'demo-ms-4',
      projectId: 'demo-proj-web',
      templateId: 'tpl-4',
      title: 'リリース',
      startDate: formatDate(17),
      iconType: 'emoji' as const,
      iconValue: '🎉',
      color: '#10b981',
      sortOrder: 1,
    },
  ] as Milestone[],

  milestoneTemplates: [
    { id: 'tpl-1', teamId: 'demo-team-video', title: '台本チェック', iconType: 'emoji' as const, iconValue: '📋', color: '#6366f1', sortOrder: 1 },
    { id: 'tpl-2', teamId: 'demo-team-video', title: '撮影日', iconType: 'emoji' as const, iconValue: '🎬', color: '#10b981', sortOrder: 2 },
    { id: 'tpl-3', teamId: 'demo-team-video', title: '納品', iconType: 'emoji' as const, iconValue: '🚀', color: '#ef4444', sortOrder: 3 },
    { id: 'tpl-4', teamId: 'demo-team-video', title: 'リリース', iconType: 'emoji' as const, iconValue: '🎉', color: '#10b981', sortOrder: 4 },
    { id: 'tpl-5', teamId: 'demo-team-video', title: '映像チェック', iconType: 'emoji' as const, iconValue: '👀', color: '#f59e0b', sortOrder: 5 },
  ] as MilestoneTemplate[],
}

// =====================
// Web制作会社向けデモデータ
// =====================
export const webProductionData = {
  team: {
    id: 'demo-team-web',
    name: 'Web制作チーム',
    description: 'コーポレートサイト・LP・ECサイト制作',
    owner_id: demoUser.id,
    created_at: formatDate(-30),
    updated_at: formatDate(0),
  } as Team,

  teamMembers: [
    { id: 'member-1', name: '高橋', avatarColor: '#8b5cf6' },
    { id: 'member-2', name: '渡辺', avatarColor: '#06b6d4' },
    { id: 'member-3', name: '伊藤', avatarColor: '#f59e0b' },
  ] as MockTeamMember[],

  projects: [
    {
      id: 'demo-proj-corp',
      name: 'D社 コーポレートサイト',
      description: 'リニューアル案件',
      color: '#8b5cf6',
      is_active: true,
      show_thumbnail: true,
      show_in_all_projects: true,
      user_id: demoUser.id,
      created_at: formatDate(-25),
      updated_at: formatDate(0),
      display_order: 1000,
      is_team_project: true,
      team_id: 'demo-team-web',
    },
    {
      id: 'demo-proj-lp',
      name: 'E社 LP制作',
      description: 'キャンペーンLP',
      color: '#06b6d4',
      is_active: true,
      show_thumbnail: true,
      show_in_all_projects: true,
      user_id: demoUser.id,
      created_at: formatDate(-10),
      updated_at: formatDate(0),
      display_order: 2000,
      is_team_project: true,
      team_id: 'demo-team-web',
    },
    // 個人プロジェクト
    {
      id: 'demo-proj-personal-web',
      name: '自分のポートフォリオ',
      description: '個人サイトのリニューアル',
      color: '#f59e0b',
      is_active: true,
      show_thumbnail: true,
      show_in_all_projects: true,
      user_id: demoUser.id,
      created_at: formatDate(-3),
      updated_at: formatDate(0),
      display_order: 3000,
      is_team_project: false,
      team_id: null,
    },
  ] as Project[],

  todos: [
    // D社 コーポレートサイト
    {
      id: 'demo-web-todo-1',
      project_id: 'demo-proj-corp',
      title: 'ワイヤーフレーム作成',
      description: '全ページのワイヤーフレームを作成',
      priority: 3,
      status: 'completed' as const,
      start_date: formatDate(-15),
      due_date: formatDate(-8),
      created_at: formatDate(-15),
      updated_at: formatDate(-8),
      completed_at: formatDate(-8),
      deleted_at: null,
      display_order: 1000,
      thumbnail_image_id: null,
      thumbnail_size: 'small' as const,
      user_id: demoUser.id,
      assigned_to_user_id: 'member-1',
      assignment_status: 'assigned' as const,
      progress_percentage: 100,
      gantt_sort_order: 1,
    },
    {
      id: 'demo-web-todo-2',
      project_id: 'demo-proj-corp',
      title: 'デザインカンプ作成',
      description: 'TOPページ・下層ページのデザイン',
      priority: 3,
      status: 'in_progress' as const,
      start_date: formatDate(-7),
      due_date: formatDate(3),
      created_at: formatDate(-7),
      updated_at: formatDate(0),
      completed_at: null,
      deleted_at: null,
      display_order: 2000,
      thumbnail_image_id: 'demo-web-image-1',
      thumbnail_size: 'small' as const,
      user_id: demoUser.id,
      assigned_to_user_id: 'member-1',
      assignment_status: 'assigned' as const,
      progress_percentage: 75,
      gantt_sort_order: 2,
    },
    {
      id: 'demo-web-todo-3',
      project_id: 'demo-proj-corp',
      title: 'コーディング',
      description: 'HTML/CSS/JSコーディング',
      priority: 3,
      status: 'pending' as const,
      start_date: formatDate(4),
      due_date: formatDate(15),
      created_at: formatDate(-5),
      updated_at: formatDate(0),
      completed_at: null,
      deleted_at: null,
      display_order: 3000,
      thumbnail_image_id: null,
      thumbnail_size: 'small' as const,
      user_id: demoUser.id,
      assigned_to_user_id: 'member-2',
      assignment_status: 'assigned' as const,
      progress_percentage: 0,
      gantt_sort_order: 3,
    },
    {
      id: 'demo-web-todo-4',
      project_id: 'demo-proj-corp',
      title: 'CMS組み込み',
      description: 'WordPressテーマ開発',
      priority: 2,
      status: 'pending' as const,
      start_date: formatDate(16),
      due_date: formatDate(22),
      created_at: formatDate(-5),
      updated_at: formatDate(0),
      completed_at: null,
      deleted_at: null,
      display_order: 4000,
      thumbnail_image_id: null,
      thumbnail_size: 'small' as const,
      user_id: demoUser.id,
      assigned_to_user_id: 'member-2',
      assignment_status: 'assigned' as const,
      progress_percentage: 0,
      gantt_sort_order: 4,
    },
    {
      id: 'demo-web-todo-5',
      project_id: 'demo-proj-corp',
      title: 'テスト・公開',
      description: '動作確認と本番公開',
      priority: 3,
      status: 'pending' as const,
      start_date: formatDate(23),
      due_date: formatDate(25),
      created_at: formatDate(-5),
      updated_at: formatDate(0),
      completed_at: null,
      deleted_at: null,
      display_order: 5000,
      thumbnail_image_id: null,
      thumbnail_size: 'small' as const,
      user_id: demoUser.id,
      assigned_to_user_id: 'member-3',
      assignment_status: 'assigned' as const,
      progress_percentage: 0,
      gantt_sort_order: 5,
    },
    // E社 LP制作
    {
      id: 'demo-web-todo-6',
      project_id: 'demo-proj-lp',
      title: '構成・コピー作成',
      description: 'LPの構成とコピーライティング',
      priority: 3,
      status: 'completed' as const,
      start_date: formatDate(-5),
      due_date: formatDate(-2),
      created_at: formatDate(-5),
      updated_at: formatDate(-2),
      completed_at: formatDate(-2),
      deleted_at: null,
      display_order: 1000,
      thumbnail_image_id: null,
      thumbnail_size: 'small' as const,
      user_id: demoUser.id,
      assigned_to_user_id: 'member-3',
      assignment_status: 'assigned' as const,
      progress_percentage: 100,
      gantt_sort_order: 1,
    },
    {
      id: 'demo-web-todo-7',
      project_id: 'demo-proj-lp',
      title: 'デザイン制作',
      description: 'ファーストビュー・各セクションデザイン',
      priority: 3,
      status: 'in_progress' as const,
      start_date: formatDate(-1),
      due_date: formatDate(5),
      created_at: formatDate(-1),
      updated_at: formatDate(0),
      completed_at: null,
      deleted_at: null,
      display_order: 2000,
      thumbnail_image_id: 'demo-web-image-2',
      thumbnail_size: 'small' as const,
      user_id: demoUser.id,
      assigned_to_user_id: 'member-1',
      assignment_status: 'assigned' as const,
      progress_percentage: 50,
      gantt_sort_order: 2,
    },
    {
      id: 'demo-web-todo-8',
      project_id: 'demo-proj-lp',
      title: 'コーディング・公開',
      description: 'レスポンシブ対応・公開',
      priority: 2,
      status: 'pending' as const,
      start_date: formatDate(6),
      due_date: formatDate(10),
      created_at: formatDate(-1),
      updated_at: formatDate(0),
      completed_at: null,
      deleted_at: null,
      display_order: 3000,
      thumbnail_image_id: null,
      thumbnail_size: 'small' as const,
      user_id: demoUser.id,
      assigned_to_user_id: 'member-2',
      assignment_status: 'assigned' as const,
      progress_percentage: 0,
      gantt_sort_order: 3,
    },
    // 自分のポートフォリオ（個人プロジェクト）
    {
      id: 'demo-web-todo-9',
      project_id: 'demo-proj-personal-web',
      title: 'デザインリニューアル',
      description: '新しいデザインに刷新',
      priority: 2,
      status: 'in_progress' as const,
      start_date: formatDate(-2),
      due_date: formatDate(7),
      created_at: formatDate(-2),
      updated_at: formatDate(0),
      completed_at: null,
      deleted_at: null,
      display_order: 1000,
      thumbnail_image_id: null,
      thumbnail_size: 'small' as const,
      user_id: demoUser.id,
      assigned_to_user_id: null,
      assignment_status: 'unassigned' as const,
      progress_percentage: 0,
      gantt_sort_order: 1,
    },
    {
      id: 'demo-web-todo-10',
      project_id: 'demo-proj-personal-web',
      title: '実績ページ追加',
      description: '最近の案件を追加',
      priority: 1,
      status: 'pending' as const,
      start_date: formatDate(8),
      due_date: formatDate(12),
      created_at: formatDate(0),
      updated_at: formatDate(0),
      completed_at: null,
      deleted_at: null,
      display_order: 2000,
      thumbnail_image_id: null,
      thumbnail_size: 'small' as const,
      user_id: demoUser.id,
      assigned_to_user_id: null,
      assignment_status: 'unassigned' as const,
      progress_percentage: 0,
      gantt_sort_order: 2,
    },
  ] as Todo[],

  milestones: [
    {
      id: 'demo-web-ms-1',
      projectId: 'demo-proj-corp',
      templateId: 'web-tpl-1',
      title: 'デザイン確定',
      startDate: formatDate(3),
      iconType: 'emoji' as const,
      iconValue: '🎨',
      color: '#8b5cf6',
      sortOrder: 1,
    },
    {
      id: 'demo-web-ms-2',
      projectId: 'demo-proj-corp',
      templateId: 'web-tpl-2',
      title: '公開日',
      startDate: formatDate(25),
      iconType: 'emoji' as const,
      iconValue: '🚀',
      color: '#10b981',
      sortOrder: 2,
    },
    {
      id: 'demo-web-ms-3',
      projectId: 'demo-proj-lp',
      templateId: 'web-tpl-2',
      title: '公開日',
      startDate: formatDate(10),
      iconType: 'emoji' as const,
      iconValue: '🚀',
      color: '#10b981',
      sortOrder: 1,
    },
  ] as Milestone[],

  milestoneTemplates: [
    { id: 'web-tpl-1', teamId: 'demo-team-web', title: 'デザイン確定', iconType: 'emoji' as const, iconValue: '🎨', color: '#8b5cf6', sortOrder: 1 },
    { id: 'web-tpl-2', teamId: 'demo-team-web', title: '公開日', iconType: 'emoji' as const, iconValue: '🚀', color: '#10b981', sortOrder: 2 },
    { id: 'web-tpl-3', teamId: 'demo-team-web', title: 'クライアント確認', iconType: 'emoji' as const, iconValue: '👀', color: '#f59e0b', sortOrder: 3 },
    { id: 'web-tpl-4', teamId: 'demo-team-web', title: 'テスト開始', iconType: 'emoji' as const, iconValue: '🧪', color: '#06b6d4', sortOrder: 4 },
  ] as MilestoneTemplate[],
}

// =====================
// 一般事務向けデモデータ
// =====================
export const officeData = {
  team: {
    id: 'demo-team-office',
    name: '総務部',
    description: '社内管理・総務業務',
    owner_id: demoUser.id,
    created_at: formatDate(-30),
    updated_at: formatDate(0),
  } as Team,

  teamMembers: [
    { id: 'member-1', name: '木村', avatarColor: '#10b981' },
    { id: 'member-2', name: '加藤', avatarColor: '#f59e0b' },
  ] as MockTeamMember[],

  projects: [
    {
      id: 'demo-proj-monthly',
      name: '月次業務',
      description: '毎月の定例業務',
      color: '#10b981',
      is_active: true,
      show_thumbnail: true,
      show_in_all_projects: true,
      user_id: demoUser.id,
      created_at: formatDate(-30),
      updated_at: formatDate(0),
      display_order: 1000,
      is_team_project: true,
      team_id: 'demo-team-office',
    },
    {
      id: 'demo-proj-event-office',
      name: '社内イベント',
      description: '忘年会・新年会の準備',
      color: '#ec4899',
      is_active: true,
      show_thumbnail: true,
      show_in_all_projects: true,
      user_id: demoUser.id,
      created_at: formatDate(-10),
      updated_at: formatDate(0),
      display_order: 2000,
      is_team_project: true,
      team_id: 'demo-team-office',
    },
  ] as Project[],

  todos: [
    // 月次業務
    {
      id: 'demo-office-todo-1',
      project_id: 'demo-proj-monthly',
      title: '経費精算締め',
      description: '今月分の経費精算を締め切り',
      priority: 3,
      status: 'completed' as const,
      start_date: formatDate(-5),
      due_date: formatDate(-3),
      created_at: formatDate(-5),
      updated_at: formatDate(-3),
      completed_at: formatDate(-3),
      deleted_at: null,
      display_order: 1000,
      thumbnail_image_id: null,
      thumbnail_size: 'small' as const,
      user_id: demoUser.id,
      assigned_to_user_id: 'member-1',
      assignment_status: 'assigned' as const,
      progress_percentage: 100,
      gantt_sort_order: 1,
    },
    {
      id: 'demo-office-todo-2',
      project_id: 'demo-proj-monthly',
      title: '給与計算',
      description: '今月の給与計算処理',
      priority: 3,
      status: 'in_progress' as const,
      start_date: formatDate(-2),
      due_date: formatDate(2),
      created_at: formatDate(-2),
      updated_at: formatDate(0),
      completed_at: null,
      deleted_at: null,
      display_order: 2000,
      thumbnail_image_id: null,
      thumbnail_size: 'small' as const,
      user_id: demoUser.id,
      assigned_to_user_id: 'member-1',
      assignment_status: 'assigned' as const,
      progress_percentage: 75,
      gantt_sort_order: 2,
    },
    {
      id: 'demo-office-todo-3',
      project_id: 'demo-proj-monthly',
      title: '月次報告書作成',
      description: '部門別の月次報告をまとめ',
      priority: 2,
      status: 'pending' as const,
      start_date: formatDate(3),
      due_date: formatDate(7),
      created_at: formatDate(0),
      updated_at: formatDate(0),
      completed_at: null,
      deleted_at: null,
      display_order: 3000,
      thumbnail_image_id: null,
      thumbnail_size: 'small' as const,
      user_id: demoUser.id,
      assigned_to_user_id: 'member-2',
      assignment_status: 'assigned' as const,
      progress_percentage: 0,
      gantt_sort_order: 3,
    },
    // 社内イベント
    {
      id: 'demo-office-todo-4',
      project_id: 'demo-proj-event-office',
      title: '会場予約',
      description: '忘年会会場の予約',
      priority: 3,
      status: 'completed' as const,
      start_date: formatDate(-7),
      due_date: formatDate(-5),
      created_at: formatDate(-7),
      updated_at: formatDate(-5),
      completed_at: formatDate(-5),
      deleted_at: null,
      display_order: 1000,
      thumbnail_image_id: 'demo-office-image-1',
      thumbnail_size: 'small' as const,
      user_id: demoUser.id,
      assigned_to_user_id: 'member-2',
      assignment_status: 'assigned' as const,
      progress_percentage: 100,
      gantt_sort_order: 1,
    },
    {
      id: 'demo-office-todo-5',
      project_id: 'demo-proj-event-office',
      title: '参加者集計',
      description: '参加人数の確認とリスト作成',
      priority: 2,
      status: 'in_progress' as const,
      start_date: formatDate(-3),
      due_date: formatDate(3),
      created_at: formatDate(-3),
      updated_at: formatDate(0),
      completed_at: null,
      deleted_at: null,
      display_order: 2000,
      thumbnail_image_id: null,
      thumbnail_size: 'small' as const,
      user_id: demoUser.id,
      assigned_to_user_id: 'member-2',
      assignment_status: 'assigned' as const,
      progress_percentage: 50,
      gantt_sort_order: 2,
    },
    {
      id: 'demo-office-todo-6',
      project_id: 'demo-proj-event-office',
      title: '景品・余興準備',
      description: 'ビンゴ景品の購入と余興の準備',
      priority: 1,
      status: 'pending' as const,
      start_date: formatDate(4),
      due_date: formatDate(10),
      created_at: formatDate(0),
      updated_at: formatDate(0),
      completed_at: null,
      deleted_at: null,
      display_order: 3000,
      thumbnail_image_id: null,
      thumbnail_size: 'small' as const,
      user_id: demoUser.id,
      assigned_to_user_id: 'member-1',
      assignment_status: 'assigned' as const,
      progress_percentage: 0,
      gantt_sort_order: 3,
    },
  ] as Todo[],

  milestones: [
    {
      id: 'demo-office-ms-1',
      projectId: 'demo-proj-monthly',
      templateId: 'office-tpl-1',
      title: '給与支払日',
      startDate: formatDate(5),
      iconType: 'emoji' as const,
      iconValue: '💰',
      color: '#10b981',
      sortOrder: 1,
    },
    {
      id: 'demo-office-ms-2',
      projectId: 'demo-proj-event-office',
      templateId: 'office-tpl-2',
      title: '忘年会当日',
      startDate: formatDate(12),
      iconType: 'emoji' as const,
      iconValue: '🎉',
      color: '#ec4899',
      sortOrder: 1,
    },
  ] as Milestone[],

  milestoneTemplates: [
    { id: 'office-tpl-1', teamId: 'demo-team-office', title: '給与支払日', iconType: 'emoji' as const, iconValue: '💰', color: '#10b981', sortOrder: 1 },
    { id: 'office-tpl-2', teamId: 'demo-team-office', title: 'イベント当日', iconType: 'emoji' as const, iconValue: '🎉', color: '#ec4899', sortOrder: 2 },
    { id: 'office-tpl-3', teamId: 'demo-team-office', title: '締め切り', iconType: 'emoji' as const, iconValue: '⏰', color: '#ef4444', sortOrder: 3 },
  ] as MilestoneTemplate[],
}

// =====================
// 個人利用向けデモデータ
// =====================
export const personalData = {
  team: null,

  teamMembers: [] as MockTeamMember[],

  projects: [
    {
      id: 'demo-proj-work',
      name: '仕事',
      description: '仕事関連のタスク',
      color: '#6366f1',
      is_active: true,
      show_thumbnail: true,
      show_in_all_projects: true,
      user_id: demoUser.id,
      created_at: formatDate(-30),
      updated_at: formatDate(0),
      display_order: 1000,
      is_team_project: false,
      team_id: null,
    },
    {
      id: 'demo-proj-private',
      name: 'プライベート',
      description: 'プライベートのタスク',
      color: '#10b981',
      is_active: true,
      show_thumbnail: true,
      show_in_all_projects: true,
      user_id: demoUser.id,
      created_at: formatDate(-30),
      updated_at: formatDate(0),
      display_order: 2000,
      is_team_project: false,
      team_id: null,
    },
    {
      id: 'demo-proj-hobby',
      name: '趣味',
      description: '趣味・学習',
      color: '#f59e0b',
      is_active: true,
      show_thumbnail: true,
      show_in_all_projects: true,
      user_id: demoUser.id,
      created_at: formatDate(-20),
      updated_at: formatDate(0),
      display_order: 3000,
      is_team_project: false,
      team_id: null,
    },
  ] as Project[],

  todos: [
    // 仕事
    {
      id: 'demo-personal-todo-1',
      project_id: 'demo-proj-work',
      title: '企画書の作成',
      description: '来週のプレゼン用資料',
      priority: 3,
      status: 'in_progress' as const,
      start_date: formatDate(-2),
      due_date: formatDate(3),
      created_at: formatDate(-2),
      updated_at: formatDate(0),
      completed_at: null,
      deleted_at: null,
      display_order: 1000,
      thumbnail_image_id: 'demo-personal-image-1',
      thumbnail_size: 'small' as const,
      user_id: demoUser.id,
      assigned_to_user_id: null,
      assignment_status: 'unassigned' as const,
      progress_percentage: 0,
      gantt_sort_order: 1,
    },
    {
      id: 'demo-personal-todo-2',
      project_id: 'demo-proj-work',
      title: 'メール返信',
      description: '取引先への返信',
      priority: 2,
      status: 'pending' as const,
      start_date: formatDate(0),
      due_date: formatDate(1),
      created_at: formatDate(0),
      updated_at: formatDate(0),
      completed_at: null,
      deleted_at: null,
      display_order: 2000,
      thumbnail_image_id: null,
      thumbnail_size: 'small' as const,
      user_id: demoUser.id,
      assigned_to_user_id: null,
      assignment_status: 'unassigned' as const,
      progress_percentage: 0,
      gantt_sort_order: 2,
    },
    {
      id: 'demo-personal-todo-3',
      project_id: 'demo-proj-work',
      title: '週報作成',
      description: '今週の活動報告',
      priority: 2,
      status: 'pending' as const,
      start_date: formatDate(4),
      due_date: formatDate(5),
      created_at: formatDate(0),
      updated_at: formatDate(0),
      completed_at: null,
      deleted_at: null,
      display_order: 3000,
      thumbnail_image_id: null,
      thumbnail_size: 'small' as const,
      user_id: demoUser.id,
      assigned_to_user_id: null,
      assignment_status: 'unassigned' as const,
      progress_percentage: 0,
      gantt_sort_order: 3,
    },
    // プライベート
    {
      id: 'demo-personal-todo-4',
      project_id: 'demo-proj-private',
      title: '歯医者予約',
      description: '定期検診',
      priority: 2,
      status: 'completed' as const,
      start_date: formatDate(-3),
      due_date: formatDate(-1),
      created_at: formatDate(-3),
      updated_at: formatDate(-1),
      completed_at: formatDate(-1),
      deleted_at: null,
      display_order: 1000,
      thumbnail_image_id: null,
      thumbnail_size: 'small' as const,
      user_id: demoUser.id,
      assigned_to_user_id: null,
      assignment_status: 'unassigned' as const,
      progress_percentage: 100,
      gantt_sort_order: 1,
    },
    {
      id: 'demo-personal-todo-5',
      project_id: 'demo-proj-private',
      title: '買い物リスト',
      description: '日用品の買い出し',
      priority: 1,
      status: 'pending' as const,
      start_date: formatDate(1),
      due_date: formatDate(2),
      created_at: formatDate(0),
      updated_at: formatDate(0),
      completed_at: null,
      deleted_at: null,
      display_order: 2000,
      thumbnail_image_id: null,
      thumbnail_size: 'small' as const,
      user_id: demoUser.id,
      assigned_to_user_id: null,
      assignment_status: 'unassigned' as const,
      progress_percentage: 0,
      gantt_sort_order: 2,
    },
    {
      id: 'demo-personal-todo-6',
      project_id: 'demo-proj-private',
      title: '部屋の掃除',
      description: '週末に大掃除',
      priority: 1,
      status: 'pending' as const,
      start_date: formatDate(5),
      due_date: formatDate(6),
      created_at: formatDate(0),
      updated_at: formatDate(0),
      completed_at: null,
      deleted_at: null,
      display_order: 3000,
      thumbnail_image_id: 'demo-personal-image-2',
      thumbnail_size: 'small' as const,
      user_id: demoUser.id,
      assigned_to_user_id: null,
      assignment_status: 'unassigned' as const,
      progress_percentage: 0,
      gantt_sort_order: 3,
    },
    // 趣味
    {
      id: 'demo-personal-todo-7',
      project_id: 'demo-proj-hobby',
      title: 'プログラミング学習',
      description: 'React入門コースを進める',
      priority: 2,
      status: 'in_progress' as const,
      start_date: formatDate(-5),
      due_date: formatDate(10),
      created_at: formatDate(-5),
      updated_at: formatDate(0),
      completed_at: null,
      deleted_at: null,
      display_order: 1000,
      thumbnail_image_id: null,
      thumbnail_size: 'small' as const,
      user_id: demoUser.id,
      assigned_to_user_id: null,
      assignment_status: 'unassigned' as const,
      progress_percentage: 0,
      gantt_sort_order: 1,
    },
    {
      id: 'demo-personal-todo-8',
      project_id: 'demo-proj-hobby',
      title: '読書',
      description: '今月の目標: 3冊',
      priority: 1,
      status: 'in_progress' as const,
      start_date: formatDate(-10),
      due_date: formatDate(20),
      created_at: formatDate(-10),
      updated_at: formatDate(0),
      completed_at: null,
      deleted_at: null,
      display_order: 2000,
      thumbnail_image_id: null,
      thumbnail_size: 'small' as const,
      user_id: demoUser.id,
      assigned_to_user_id: null,
      assignment_status: 'unassigned' as const,
      progress_percentage: 0,
      gantt_sort_order: 2,
    },
  ] as Todo[],

  milestones: [] as Milestone[],

  milestoneTemplates: [
    { id: 'personal-tpl-1', teamId: '', title: '締め切り', iconType: 'emoji' as const, iconValue: '⏰', color: '#ef4444', sortOrder: 1 },
    { id: 'personal-tpl-2', teamId: '', title: '目標達成', iconType: 'emoji' as const, iconValue: '🎯', color: '#10b981', sortOrder: 2 },
  ] as MilestoneTemplate[],
}

// デモタイプに応じたデータを取得
export function getDemoData(demoType: 'video' | 'web' | 'office' | 'personal') {
  switch (demoType) {
    case 'video':
      return videoProductionData
    case 'web':
      return webProductionData
    case 'office':
      return officeData
    case 'personal':
      return personalData
    default:
      return personalData
  }
}

// デモタイプのラベル
export const demoTypeLabels = {
  video: '映像制作会社',
  web: 'Web制作会社',
  office: '一般事務',
  personal: '個人利用',
}

// 現在のデモタイプを保持（milestoneServiceからアクセス用）
let currentDemoType: 'video' | 'web' | 'office' | 'personal' | null = null

// デモモード用の可変マイルストーンストレージ（CRUD用）
let demoMilestones: Milestone[] = []

export function setCurrentDemoType(type: 'video' | 'web' | 'office' | 'personal' | null) {
  currentDemoType = type
  // デモタイプが設定された時にマイルストーンを初期化
  if (type) {
    const data = getDemoData(type)
    demoMilestones = [...data.milestones]
  } else {
    demoMilestones = []
  }
}

export function getCurrentDemoType() {
  return currentDemoType
}

// Milestoneをsnake_case形式（ProjectMilestone互換）に変換
function convertMilestoneToSnakeCase(m: Milestone) {
  return {
    id: m.id,
    project_id: m.projectId,
    template_id: m.templateId,
    title: m.title,
    start_date: m.startDate,
    end_date: m.endDate || null,
    icon_type: m.iconType,
    icon_value: m.iconValue,
    color: m.color,
    sort_order: m.sortOrder,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  }
}

// デモ用マイルストーンをProjectMilestone形式に変換して取得
export function getDemoMilestonesByProjectId(projectId: string) {
  if (!currentDemoType) return []
  return demoMilestones
    .filter(m => m.projectId === projectId)
    .map(convertMilestoneToSnakeCase)
}

// デモ用マイルストーンを複数プロジェクト分取得
export function getDemoMilestonesByProjectIds(projectIds: string[]) {
  if (!currentDemoType || projectIds.length === 0) return []
  return demoMilestones
    .filter(m => projectIds.includes(m.projectId))
    .map(convertMilestoneToSnakeCase)
}

// デモ用マイルストーンテンプレートを取得
export function getDemoMilestoneTemplates() {
  if (!currentDemoType) return []
  const data = getDemoData(currentDemoType)
  return data.milestoneTemplates.map(t => ({
    id: t.id,
    team_id: t.teamId || null,
    user_id: null,
    title: t.title,
    icon_type: t.iconType,
    icon_value: t.iconValue,
    color: t.color,
    sort_order: t.sortOrder,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  }))
}

// =====================================================
// デモ用マイルストーンCRUD関数
// =====================================================

// snake_case形式からcamelCase形式（内部Milestone）に変換
interface CreateMilestoneInput {
  project_id: string
  template_id: string | null
  title: string
  start_date: string
  end_date: string | null
  icon_type: 'emoji' | 'lucide'
  icon_value: string
  color: string
  sort_order: number
}

// デモ用マイルストーンを作成
export function addDemoMilestone(milestone: CreateMilestoneInput) {
  const newMilestone: Milestone = {
    id: `demo-ms-${Date.now()}-${Math.random().toString(36).substring(7)}`,
    projectId: milestone.project_id,
    templateId: milestone.template_id || '',
    title: milestone.title,
    startDate: milestone.start_date,
    endDate: milestone.end_date || undefined,
    iconType: milestone.icon_type,
    iconValue: milestone.icon_value,
    color: milestone.color,
    sortOrder: milestone.sort_order,
  }
  demoMilestones.push(newMilestone)
  return convertMilestoneToSnakeCase(newMilestone)
}

// デモ用マイルストーンを更新
export function updateDemoMilestone(id: string, updates: Partial<CreateMilestoneInput>) {
  const index = demoMilestones.findIndex(m => m.id === id)
  if (index === -1) {
    throw new Error(`Milestone not found: ${id}`)
  }

  const current = demoMilestones[index]
  const updated: Milestone = {
    ...current,
    ...(updates.title !== undefined && { title: updates.title }),
    ...(updates.start_date !== undefined && { startDate: updates.start_date }),
    ...(updates.end_date !== undefined && { endDate: updates.end_date || undefined }),
    ...(updates.icon_type !== undefined && { iconType: updates.icon_type }),
    ...(updates.icon_value !== undefined && { iconValue: updates.icon_value }),
    ...(updates.color !== undefined && { color: updates.color }),
    ...(updates.sort_order !== undefined && { sortOrder: updates.sort_order }),
    ...(updates.template_id !== undefined && { templateId: updates.template_id || '' }),
  }
  demoMilestones[index] = updated
  return convertMilestoneToSnakeCase(updated)
}

// デモ用マイルストーンを削除
export function deleteDemoMilestone(id: string): void {
  const index = demoMilestones.findIndex(m => m.id === id)
  if (index !== -1) {
    demoMilestones.splice(index, 1)
  }
}

// デモ用マイルストーンをリセット（デモ終了時に呼び出し）
export function resetDemoMilestones(): void {
  demoMilestones = []
}
